/**
 * Track what the operator has typed on the current terminal line so the parent
 * component can open a command record (`beginRecord`) when Enter is pressed.
 *
 * Fed from xterm's `onData`, whose chunks carry raw keystrokes, pasted text, and
 * the terminal's own escape-sequence replies to host queries (arrow keys, DSR
 * cursor-position reports, device attributes). None of those escape sequences are
 * part of a typed command, so a chunk that starts with ESC is ignored wholesale;
 * within a plain chunk only printable ASCII accumulates, backspace/DEL pops, and
 * CR/LF commits the trimmed line. A pasted block with embedded newlines commits
 * one command per line.
 */
export function trackCommandInput(
  buffer: string,
  data: string
): { buffer: string; commands: string[] } {
  if (data.charCodeAt(0) === 0x1b) return { buffer, commands: [] };

  const commands: string[] = [];
  let next = buffer;

  for (const ch of data) {
    if (ch === '\r' || ch === '\n') {
      const typed = next.trim();
      if (typed.length > 0) commands.push(typed);
      next = '';
    } else if (ch === '\x7f' || ch === '\b') {
      next = next.slice(0, -1);
    } else {
      const code = ch.charCodeAt(0);
      if (code >= 32 && code < 127) next += ch;
    }
  }

  return { buffer: next, commands };
}

/**
 * A device-report reply the terminal sends back in answer to a host capability
 * query: DSR cursor-position (`CSI ... R`), primary/secondary device attributes
 * (`CSI [?>] ... c`), and status (`CSI ... n`). Interactive tools block on these
 * — e.g. evil-winrm's Reline emits `CSI 6n` on startup and waits for the
 * `CSI <row>;<col>R` reply.
 *
 * xterm delivers each report as a single `onData` chunk, so matching the whole
 * chunk is reliable. Read-only AI terminals relay ONLY these replies to the
 * shared PTY (so tools the AI launches don't hang) while never forwarding
 * operator keystrokes or paste — a typed key or arrow sequence never matches.
 */
const TERMINAL_REPORT = /^\x1b\[[?>]?[0-9;]*[Rcn]$/;

export function isTerminalReport(data: string): boolean {
  return TERMINAL_REPORT.test(data);
}
